import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { TestsService } from '@generated/services/tests.service';
import { AgentsService } from '@generated/services/agents.service';
import { TestSummary, TestType, TestRunSummary, TestRunSessionDto, TestStatus } from '@generated/models';
import { NotificationService } from '@core/services/notification.service';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { RunTestDrawerComponent } from '@shared/components/run-test-drawer/run-test-drawer.component';
import { AssistantContextService } from '@shared/services/assistant-context.service';
import { Signalr } from '@core/services/signalr';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';

@Component({
  selector: 'app-tests',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TagModule,
    TooltipModule,
    SelectModule,
    TabsModule,
    DeleteConfirmationComponent,
    RunTestDrawerComponent
],
  templateUrl: './tests.component.html',
  styleUrl: './tests.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestsComponent implements OnInit, OnDestroy {
  private testsService = inject(TestsService);
  private agentsService = inject(AgentsService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private assistantContext = inject(AssistantContextService);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);

  tests = signal<TestSummary[]>([]);
  loading = signal(true);
  searchTerm = signal('');
  typeFilter = signal<TestType | null>(null);

  // ---- Past Run Analysis tab ----
  testRuns = signal<TestRunSummary[]>([]);
  runsLoading = signal(false);
  runsNextCursor = signal<string | null>(null);
  private runsLoaded = false;
  /** agent_id → title, lazily resolved for the runs table. */
  private agentNames = signal<Record<string, string>>({});

  // Row expansion: sessions per test run, lazily loaded on expand.
  // Same pattern as model-selector: inline expanded rows toggled by id.
  expandedRows = signal<Record<string, boolean>>({});
  runSessions = signal<Record<string, TestRunSessionDto[]>>({});
  runSessionsLoading = signal<Record<string, boolean>>({});

  // Run-test drawer state
  runDrawerVisible = signal(false);
  runTestId = signal<string | null>(null);
  runTestName = signal<string>('');

  /** Активный таб берём из URL: /tests → 'all', /tests/runs → 'past'. */
  activeTab = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => (e.urlAfterRedirects.endsWith('/runs') ? 'past' : 'all') as 'all' | 'past'),
      startWith((this.router.url.endsWith('/runs') ? 'past' : 'all') as 'all' | 'past'),
    ),
    { initialValue: (this.router.url.endsWith('/runs') ? 'past' : 'all') as 'all' | 'past' },
  );

  showDeleteDialog = signal(false);
  isDeleting = signal(false);
  testToDelete = signal<TestSummary | null>(null);

  readonly deleteTitle = $localize`:@@tests.delete.title:Delete test`;
  deleteSubtitle = computed(() => {
    const name = this.testToDelete()?.name || '';
    return $localize`:@@tests.delete.subtitle:Are you sure you want to delete '${name}:name:'? This action cannot be undone.`;
  });

  readonly typeOptions: Array<{ label: string; value: TestType | null }> = [
    { label: $localize`:@@tests.filter.all:All types`, value: null },
    { label: $localize`:@@tests.type.simulation:Simulation`, value: 'Simulation' },
    { label: $localize`:@@tests.type.e2e:End-to-end`, value: 'E2E' },
  ];

  filteredTests = computed(() => {
    const all = this.tests();
    const term = this.searchTerm().toLowerCase();
    const type = this.typeFilter();
    return all.filter(t => {
      if (type && t.type !== type) return false;
      if (!term) return true;
      return (t.name || '').toLowerCase().includes(term) || (t.description || '').toLowerCase().includes(term);
    });
  });

  constructor() {
    // Публикуем в assistant context то, что показывает активная вкладка.
    // Один компонент на два адреса: /tests — сценарии, /tests/runs —
    // история прогонов. Раньше контекст был один («Tests list») на оба, и
    // на /tests/runs агент получал список тестов вместо прогонов — то
    // есть историю прогонов через интерфейс прочитать было нельзя
    // (SKN-473).
    effect(() => {
      if (this.activeTab() === 'past') {
        if (this.runsLoading()) return;
        const runs = this.testRuns();
        this.assistantContext.set({
          description: 'Test runs history — results of past evaluation runs',
          value: {
            type: 'test-runs-list',
            count: runs.length,
            items: runs.map(r => ({
              id: r.id,
              agentId: r.agent_id,
              createdAt: r.created_at,
              total: r.total_sessions,
              success: r.success_sessions,
              failed: r.failed_sessions,
              processing: r.processing_sessions,
            })),
          },
        });
        return;
      }

      if (this.loading()) return;
      const items = this.tests();
      this.assistantContext.set({
        description: 'Tests list',
        value: { type: 'tests-list', count: items.length, items: items.map(t => ({ id: t.id, name: t.name, type: t.type })) },
      });
    });

    // Лениво грузим тест-раны при первом переходе на вкладку "past".
    effect(() => {
      if (this.activeTab() === 'past' && !this.runsLoaded) {
        this.runsLoaded = true;
        this.loadAgentNames();
        this.loadTestRuns();
      }
    });
  }

  ngOnInit(): void {
    this.loadTests();

    // Новый тест создан где-то — перезагружаем список (нужны name/type/updated_at).
    this.signalr.testCreated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadTests());

    // Тест-ран обновлён — патчим его в списке по id, если он там есть.
    this.signalr.testRunUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((run) => {
        if (!run?.id) return;
        this.testRuns.update(list =>
          list.some(r => r.id === run.id)
            ? list.map(r => r.id === run.id ? { ...r, ...run } : r)
            : list,
        );
      });

    // Сессия обновлена — патчим её внутри раскрытого рана по id.
    this.signalr.agentSessionUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((session) => {
        const runId = session?.test_run_id;
        if (!session?.id || !runId) return;
        this.runSessions.update(map => {
          const bucket = map[runId];
          if (!bucket || !bucket.some(s => s.id === session.id)) return map;
          return {
            ...map,
            [runId]: bucket.map(s => s.id === session.id ? { ...s, ...session } : s),
          };
        });
      });
  }

  ngOnDestroy(): void {
    this.assistantContext.clear();
  }

  loadTests(): void {
    this.loading.set(true);
    const type = this.typeFilter() ?? undefined;
    this.testsService.apiTestsGet({ type }).subscribe({
      next: (result) => {
        this.tests.set(result.tests || []);
        this.loading.set(false);
      },
      error: () => {
        this.notificationService.showError($localize`:@@tests.notify.loadFail:Failed to load tests`);
        this.loading.set(false);
      },
    });
  }

  onTypeFilterChange(): void {
    this.loadTests();
  }

  // ---- Past Run Analysis ----

  private loadAgentNames(): void {
    this.agentsService.apiAgentsGet().subscribe({
      next: (result) => {
        const map: Record<string, string> = {};
        for (const a of result.agents ?? []) {
          if (a.id) map[a.id] = a.title || '';
        }
        this.agentNames.set(map);
      },
      // Тихо — имена агентов лишь украшают таблицу, фейл не критичен.
      error: () => {},
    });
  }

  loadTestRuns(append = false): void {
    if (this.runsLoading()) return;
    this.runsLoading.set(true);
    const cursor = append ? this.runsNextCursor() ?? undefined : undefined;
    this.testsService.apiTestRunsGet({ cursor, limit: 50 }).subscribe({
      next: (result) => {
        const items = result.items ?? [];
        this.testRuns.update(prev => append ? [...prev, ...items] : items);
        this.runsNextCursor.set(result.next_cursor ?? null);
        this.runsLoading.set(false);
      },
      error: () => {
        this.notificationService.showError($localize`:@@tests.runs.notify.loadFail:Failed to load test runs`);
        this.runsLoading.set(false);
      },
    });
  }

  refreshTestRuns(): void {
    this.runsNextCursor.set(null);
    this.loadTestRuns(false);
  }

  loadMoreTestRuns(): void {
    if (this.runsNextCursor()) this.loadTestRuns(true);
  }

  agentName(agentId?: string): string {
    if (!agentId) return '-';
    return this.agentNames()[agentId] || agentId;
  }

  toggleRunExpansion(run: TestRunSummary): void {
    const id = run.id;
    if (!id) return;

    if (this.expandedRows()[id]) {
      this.expandedRows.update(m => {
        const next = { ...m };
        delete next[id];
        return next;
      });
      return;
    }

    this.expandedRows.update(m => ({ ...m, [id]: true }));

    // Lazily load sessions for this run once.
    if (this.runSessions()[id] || this.runSessionsLoading()[id]) return;
    this.runSessionsLoading.update(m => ({ ...m, [id]: true }));
    this.testsService.apiTestRunsIdGet({ id }).subscribe({
      next: (result) => {
        this.runSessions.update(m => ({ ...m, [id]: result.sessions ?? [] }));
        this.runSessionsLoading.update(m => ({ ...m, [id]: false }));
      },
      error: () => {
        this.notificationService.showError($localize`:@@tests.runs.notify.sessionsFail:Failed to load test run sessions`);
        this.runSessionsLoading.update(m => ({ ...m, [id]: false }));
        this.expandedRows.update(m => {
          const next = { ...m };
          delete next[id];
          return next;
        });
      },
    });
  }

  sessionsFor(runId: string): TestRunSessionDto[] {
    return this.runSessions()[runId] ?? [];
  }

  testStatusSeverity(status?: TestStatus | null): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (status) {
      case 'Success': return 'success';
      case 'Failed': return 'danger';
      case 'Processing': return 'warn';
      default: return 'secondary';
    }
  }

  testStatusLabel(status?: TestStatus | null): string {
    switch (status) {
      case 'Success': return $localize`:@@tests.runs.status.success:Success`;
      case 'Failed': return $localize`:@@tests.runs.status.failed:Failed`;
      case 'Processing': return $localize`:@@tests.runs.status.processing:Processing`;
      case 'Initial': return $localize`:@@tests.runs.status.initial:Initial`;
      default: return '-';
    }
  }

  runStatusSeverity(run: TestRunSummary): 'success' | 'danger' | 'warn' {
    if ((run.processing_sessions ?? 0) > 0) return 'warn';
    if ((run.failed_sessions ?? 0) > 0) return 'danger';
    return 'success';
  }

  runStatusLabel(run: TestRunSummary): string {
    if ((run.processing_sessions ?? 0) > 0) return $localize`:@@tests.runs.status.processing:Processing`;
    if ((run.failed_sessions ?? 0) > 0) return $localize`:@@tests.runs.status.hasFailures:Has failures`;
    return $localize`:@@tests.runs.status.success:Success`;
  }

  createNewTest(): void {
    this.router.navigate(['/tests/new']);
  }

  onTabChange(value: 'all' | 'past'): void {
    this.router.navigate(value === 'past' ? ['/tests/runs'] : ['/tests']);
  }

  editTest(test: TestSummary): void {
    if (!test.id) return;
    this.router.navigate(['/tests', test.id]);
  }

  runTest(test: TestSummary): void {
    if (!test.id) return;
    this.runTestId.set(test.id);
    this.runTestName.set(test.name || '');
    this.runDrawerVisible.set(true);
  }

  askDelete(test: TestSummary, event: Event): void {
    event.stopPropagation();
    this.testToDelete.set(test);
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirm(): void {
    const test = this.testToDelete();
    if (!test?.id) return;
    this.isDeleting.set(true);
    this.testsService.apiTestsIdDelete({ id: test.id }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@tests.delete.success:Test deleted`);
        this.showDeleteDialog.set(false);
        this.isDeleting.set(false);
        this.testToDelete.set(null);
        this.loadTests();
      },
      error: () => {
        this.notificationService.showError($localize`:@@tests.delete.error:Failed to delete test`);
        this.isDeleting.set(false);
      },
    });
  }

  typeSeverity(type?: TestType): 'info' | 'success' | 'warn' {
    switch (type) {
      case 'Simulation': return 'info';
      // E2E — настоящая сессия с аудио и списанием, его стоит
      // отличать от text-only прогона с первого взгляда.
      case 'E2E': return 'warn';
      default: return 'info';
    }
  }

  typeLabel(type?: TestType): string {
    switch (type) {
      case 'Simulation': return $localize`:@@tests.type.simulation:Simulation`;
      case 'E2E': return $localize`:@@tests.type.e2e:End-to-end`;
      default: return '';
    }
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }
}
